import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, FormSelect, Modal } from '@/components/UI';
import DataTable from '@/components/UI/DataTable';
import type { ScreenerOptimizerFeatureAnalysisRow } from '@/types/appRouter';
import type { FeatureImpactComboRow, FeatureImpactSimpleRow, OptimizerResultRow, OptimizerResultSortBy } from './types';
import { featureAnalysisColumns } from './featureAnalysisColumns';
import { comboFeatureColumns, needleMoverColumns } from './featureImpactColumns';
import { buildComboFeatureRows, formatDecimal, formatPct01, formatPct01Number, formatSignedPct, toNumber } from './helpers';

interface FeatureImpactModalProps {
    isOpen: boolean;
    selectedJobId: number | null;
    featureImpactTab: 'needleMovers' | 'univariate' | 'combos';
    setFeatureImpactTab: (tab: 'needleMovers' | 'univariate' | 'combos') => void;
    featureImpactSelectedFeatureKey: string | null;
    setFeatureImpactSelectedFeatureKey: (featureKey: string | null) => void;
    results: OptimizerResultRow[];
    features: ScreenerOptimizerFeatureAnalysisRow[];
    needleMovers: FeatureImpactSimpleRow[];
    featureAnalysisIsError: boolean;
    featureAnalysisErrorMessage: string | undefined;
    featureAnalysisIsFetching: boolean;
    featureImpactSimpleIsError: boolean;
    featureImpactSimpleErrorMessage: string | undefined;
    featureImpactSimpleIsFetching: boolean;
    featureImpactResultsIsError: boolean;
    featureImpactResultsErrorMessage: string | undefined;
    featureImpactResultsIsFetching: boolean;
    sortBy: OptimizerResultSortBy | undefined;
    sortDirection: 'DESC' | 'ASC' | undefined;
    onClose: () => void;
}

const FeatureImpactModal = ({
    isOpen,
    selectedJobId,
    featureImpactTab,
    setFeatureImpactTab,
    featureImpactSelectedFeatureKey,
    setFeatureImpactSelectedFeatureKey,
    results,
    features,
    needleMovers,
    featureAnalysisIsError,
    featureAnalysisErrorMessage,
    featureAnalysisIsFetching,
    featureImpactSimpleIsError,
    featureImpactSimpleErrorMessage,
    featureImpactSimpleIsFetching,
    featureImpactResultsIsError,
    featureImpactResultsErrorMessage,
    featureImpactResultsIsFetching,
    sortBy,
    sortDirection,
    onClose
}: FeatureImpactModalProps) => {
    const [featureImpactTopN, setFeatureImpactTopN] = useState<number>(20);
    const [featureImpactShowThresholdScan, setFeatureImpactShowThresholdScan] = useState<boolean>(false);

    useEffect(() => {
        setFeatureImpactShowThresholdScan(false);
    }, [featureImpactSelectedFeatureKey]);

    const comboFeatureRows = useMemo(
        (): FeatureImpactComboRow[] => buildComboFeatureRows(results, featureImpactTopN),
        [featureImpactTopN, results]
    );
    const featureImpactResultsUnavailable = featureImpactResultsIsError && results.length === 0;
    const featureImpactResultsLoading = featureImpactResultsIsFetching && results.length === 0;

    const selectedUnivariateFeature = useMemo(() => {
        if (!featureImpactSelectedFeatureKey) return null;
        return features.find((f) => f.featureKey === featureImpactSelectedFeatureKey) ?? null;
    }, [featureImpactSelectedFeatureKey, features]);

    const selectedComboFeature = useMemo(() => {
        if (!featureImpactSelectedFeatureKey) return null;
        return comboFeatureRows.find((r) => r.featureKey === featureImpactSelectedFeatureKey) ?? null;
    }, [comboFeatureRows, featureImpactSelectedFeatureKey]);

    const selectedNeedleMoverFeature = useMemo(() => {
        if (!featureImpactSelectedFeatureKey) return null;
        return needleMovers.find((f) => f.featureKey === featureImpactSelectedFeatureKey) ?? null;
    }, [featureImpactSelectedFeatureKey, needleMovers]);

    return (
        <Modal
            isOpen={isOpen}
            size="xl"
            title={selectedJobId !== null ? `Feature Impact (Job #${selectedJobId})` : 'Feature Impact'}
            onClose={onClose}
        >
            <div className="text-zinc-400 text-xs mb-3">
                Needle movers ranks features by overall holdout impact (threshold-agnostic). Univariate shows each feature’s best standalone
                threshold. In top combos shows which features appear in the current top results (based on rule JSON), which is often more
                actionable for understanding what drives the winners.
            </div>

            <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                    <Button
                        size="sm"
                        variant={featureImpactTab === 'needleMovers' ? 'primary' : 'secondary'}
                        onClick={() => setFeatureImpactTab('needleMovers')}
                    >
                        Needle movers
                    </Button>
                    <Button
                        size="sm"
                        variant={featureImpactTab === 'univariate' ? 'primary' : 'secondary'}
                        onClick={() => setFeatureImpactTab('univariate')}
                    >
                        Top features
                    </Button>
                    <Button
                        size="sm"
                        variant={featureImpactTab === 'combos' ? 'primary' : 'secondary'}
                        onClick={() => setFeatureImpactTab('combos')}
                    >
                        In top combos
                    </Button>
                </div>

                {featureImpactTab === 'combos' && (
                    <div className="flex items-center gap-2">
                        <div className="text-zinc-500 text-xs">Top N</div>
                        <FormSelect
                            noMargin
                            fullWidth={false}
                            id="feature-impact-top-n"
                            options={[
                                { value: '10', label: '10' },
                                { value: '20', label: '20' },
                                { value: '50', label: '50' },
                                { value: '100', label: '100' }
                            ]}
                            size="sm"
                            value={String(featureImpactTopN)}
                            onChange={(e) => setFeatureImpactTopN(Math.max(1, Math.floor(Number(e.target.value) || 20)))}
                        />
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                    {featureImpactTab === 'needleMovers' && (
                        <>
                            {featureImpactSimpleIsError && (
                                <div className="text-red-300 text-sm mb-2">
                                    Failed to load needle movers: {featureImpactSimpleErrorMessage ?? 'Unknown error'}
                                </div>
                            )}
                            {featureImpactSimpleIsFetching && needleMovers.length === 0 ? (
                                <div className="text-zinc-400 text-sm">Loading needle movers…</div>
                            ) : needleMovers.length === 0 && !featureImpactSimpleIsError ? (
                                <div className="text-zinc-400 text-sm">No needle mover data yet.</div>
                            ) : (
                                <DataTable
                                    allowOverflow
                                    search
                                    columns={needleMoverColumns}
                                    data={needleMovers}
                                    pagination={{ enabled: true, pageSize: 15 }}
                                    onRowClick={(row) => setFeatureImpactSelectedFeatureKey(row.featureKey)}
                                />
                            )}
                        </>
                    )}

                    {featureImpactTab === 'univariate' && (
                        <>
                            {featureAnalysisIsError && (
                                <div className="text-red-300 text-sm mb-2">
                                    Failed to load features: {featureAnalysisErrorMessage ?? 'Unknown error'}
                                </div>
                            )}
                            {featureAnalysisIsFetching && features.length === 0 ? (
                                <div className="text-zinc-400 text-sm">Loading feature analysis…</div>
                            ) : features.length === 0 && !featureAnalysisIsError ? (
                                <div className="text-zinc-400 text-sm">No feature analysis data yet.</div>
                            ) : (
                                <DataTable
                                    allowOverflow
                                    search
                                    columns={featureAnalysisColumns}
                                    data={features}
                                    pagination={{ enabled: true, pageSize: 15 }}
                                    onRowClick={(row) => setFeatureImpactSelectedFeatureKey(row.featureKey)}
                                />
                            )}
                        </>
                    )}

                    {featureImpactTab === 'combos' && (
                        <>
                            <div className="text-zinc-500 text-xs mb-2">
                                Based on current result sorting ({sortBy ?? 'default'} {sortDirection ?? ''}). “In Top” is presence per
                                result, not total condition occurrences.
                            </div>
                            {featureImpactResultsIsError && (
                                <div className="text-red-300 text-sm mb-2">
                                    Failed to load top results: {featureImpactResultsErrorMessage ?? 'Unknown error'}
                                </div>
                            )}
                            {!featureImpactResultsUnavailable &&
                                (featureImpactResultsLoading ? (
                                    <div className="text-zinc-400 text-sm">Loading top results…</div>
                                ) : comboFeatureRows.length === 0 ? (
                                    <div className="text-zinc-400 text-sm">No combo feature data yet (need results with rule JSON).</div>
                                ) : (
                                    <DataTable
                                        allowOverflow
                                        search
                                        columns={comboFeatureColumns}
                                        data={comboFeatureRows}
                                        pagination={{ enabled: true, pageSize: 20 }}
                                        onRowClick={(row) => setFeatureImpactSelectedFeatureKey(row.featureKey)}
                                    />
                                ))}
                        </>
                    )}
                </div>

                <div className="lg:col-span-1">
                    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
                        <h5 className="text-white font-semibold mb-2">Details</h5>
                        {!featureImpactSelectedFeatureKey ? (
                            <div className="text-zinc-400 text-sm">
                                Click a feature to see its overall holdout impact, best threshold, and how it appears in top results.
                            </div>
                        ) : (
                            <>
                                <div className="text-white text-sm font-semibold break-words mb-2">{featureImpactSelectedFeatureKey}</div>

                                {selectedNeedleMoverFeature && (
                                    <div className="grid grid-cols-2 gap-2 text-xs text-zinc-400 mb-3">
                                        <div>
                                            Impact:{' '}
                                            <span className="text-white">
                                                {formatPct01Number(toNumber(selectedNeedleMoverFeature.impactScore), 1)}
                                            </span>
                                        </div>
                                        <div>
                                            Direction:{' '}
                                            <Badge
                                                variant={
                                                    selectedNeedleMoverFeature.direction === 'positive'
                                                        ? 'success'
                                                        : selectedNeedleMoverFeature.direction === 'negative'
                                                          ? 'danger'
                                                          : selectedNeedleMoverFeature.direction === 'mixed'
                                                            ? 'warning'
                                                            : 'secondary'
                                                }
                                            >
                                                {selectedNeedleMoverFeature.direction}
                                            </Badge>
                                        </div>
                                        <div>
                                            Δ:{' '}
                                            <span
                                                className={
                                                    toNumber(selectedNeedleMoverFeature.delta) >= 0 ? 'text-green-400' : 'text-red-400'
                                                }
                                            >
                                                {formatSignedPct(toNumber(selectedNeedleMoverFeature.delta), 2)}
                                            </span>
                                        </div>
                                        <div>
                                            HO N:{' '}
                                            <span className="text-white">
                                                {selectedNeedleMoverFeature.holdoutSampleCount.toLocaleString()}
                                            </span>
                                        </div>
                                        <div>
                                            Missing:{' '}
                                            <span className="text-white">{formatPct01(selectedNeedleMoverFeature.missingRate, 1)}</span>
                                        </div>
                                        <div>
                                            Bins: <span className="text-white">{selectedNeedleMoverFeature.binCountUsed}</span>
                                        </div>
                                    </div>
                                )}

                                {selectedUnivariateFeature && (
                                    <>
                                        <div className="grid grid-cols-2 gap-2 text-xs text-zinc-400 mb-3">
                                            <div>
                                                Best op: <span className="text-white">{selectedUnivariateFeature.bestOperator}</span>
                                            </div>
                                            <div>
                                                Threshold:{' '}
                                                <span className="text-white">{selectedUnivariateFeature.bestThreshold.toFixed(4)}</span>
                                            </div>
                                            <div>
                                                Sweet:{' '}
                                                <span className="text-white">
                                                    {formatDecimal(selectedUnivariateFeature.bestSweetScore, 4)}
                                                </span>
                                            </div>
                                            <div>
                                                Lift:{' '}
                                                <span
                                                    className={
                                                        toNumber(selectedUnivariateFeature.bestLift) >= 0
                                                            ? 'text-green-400'
                                                            : 'text-red-400'
                                                    }
                                                >
                                                    {formatDecimal(selectedUnivariateFeature.bestLift, 2)}%
                                                </span>
                                            </div>
                                            <div>
                                                Trades:{' '}
                                                <span className="text-white">
                                                    {selectedUnivariateFeature.bestTradeCount.toLocaleString()}
                                                </span>
                                            </div>
                                            <div>
                                                Win:{' '}
                                                <span className="text-white">{formatPct01(selectedUnivariateFeature.bestWinRate, 1)}</span>
                                            </div>
                                            <div>
                                                Coverage:{' '}
                                                <span className="text-white">{formatPct01(selectedUnivariateFeature.bestCoverage, 1)}</span>
                                            </div>
                                            <div>
                                                Missing:{' '}
                                                <span className="text-white">{formatPct01(selectedUnivariateFeature.missingRate, 1)}</span>
                                            </div>
                                        </div>

                                        {selectedUnivariateFeature.thresholdScanJson && (
                                            <>
                                                <Button
                                                    size="sm"
                                                    variant="secondary"
                                                    onClick={() => setFeatureImpactShowThresholdScan((v) => !v)}
                                                >
                                                    {featureImpactShowThresholdScan ? 'Hide threshold scan' : 'Show threshold scan'}
                                                </Button>
                                                {featureImpactShowThresholdScan && (
                                                    <pre className="mt-2 bg-zinc-950 text-zinc-200 text-xs p-3 rounded overflow-auto max-h-[35vh] whitespace-pre-wrap">
                                                        {selectedUnivariateFeature.thresholdScanJson}
                                                    </pre>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}

                                {selectedComboFeature && (
                                    <div className="text-zinc-400 text-xs mb-3">
                                        Seen in top results: <span className="text-white">{selectedComboFeature.inTopResultsCount}</span>/
                                        <span className="text-white">{selectedComboFeature.topResultsN}</span>
                                        {selectedComboFeature.topOperator ? (
                                            <>
                                                {' '}
                                                · common op: <span className="text-white">{selectedComboFeature.topOperator}</span>
                                            </>
                                        ) : null}
                                    </div>
                                )}

                                {selectedComboFeature?.examples && selectedComboFeature.examples.length > 0 && (
                                    <>
                                        <div className="text-zinc-500 text-xs mb-1">Examples in top results</div>
                                        <div className="space-y-2 max-h-[45vh] overflow-auto pr-1">
                                            {selectedComboFeature.examples.map((ex, idx) => (
                                                <div className="text-xs text-zinc-300" key={`${ex.resultId}-${idx}`}>
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span className="text-zinc-400">
                                                            resultId={ex.resultId}
                                                            {ex.resultScore !== null ? ` (score ${ex.resultScore.toFixed(4)})` : ''}
                                                        </span>
                                                        <span className="text-zinc-400">
                                                            lift {ex.lift !== null ? formatSignedPct(ex.lift, 2) : '-'}
                                                            {ex.holdoutLift !== null ? ` · HO ${formatSignedPct(ex.holdoutLift, 2)}` : ''}
                                                        </span>
                                                    </div>
                                                    <div className="text-white break-words">{ex.condition}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}

                                {!selectedNeedleMoverFeature && !selectedUnivariateFeature && !selectedComboFeature && (
                                    <div className="text-zinc-400 text-sm">No details available for this feature yet.</div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="text-zinc-500 text-[11px] mt-3">
                        Notes: needle movers can disagree with univariate ranking and combo performance due to feature interactions. Prefer
                        features that show up consistently in top results and have positive holdout lift where available.
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default FeatureImpactModal;
